import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { Route, Routes, BrowserRouter, useNavigate } from 'react-router-dom';
import { withRouterHooks } from 'common/routing_utils/withRouterHookWrapper';
import airbrake from 'common/airbrake';
import { addLocation, locationChanged, Dispatcher } from 'common/explore_grid/redux/actions';
import create, { LocationParams, ExternalVQEData, VQEColumn } from 'common/explore_grid/redux/store';
import { ExploreGridApp } from 'common/explore_grid/components/App';
import { option, some, none } from 'ts-option';
import _ from 'lodash';
import { viewColumnToVQEColumn } from 'common/explore_grid/lib/selectors';
import { Tab } from 'common/explore_grid/types';

const BASE_PATH = '/:locale?/:datasetOrCategory/:name/:fourfour/explore';

// Component to handle route effects (replaces onEnter)
const RouteHandler = withRouterHooks(
  ({
    match,
    location,
    store,
  }: {
    match: any;
    location: Location;
    preserveCompiledQuery?: boolean;
    store: any;
  }) => {
    useEffect(() => {
      const dispatchLocation = (params: any, query: any) => {
        const preserveCompiledQuery = !!params.subTab;
        const locationParams: LocationParams = {
          queryString: option(params.queryString),
          tab: params.tab || Tab.Filter,
          subTab: option(params.subTab),
          queryParams: new URLSearchParams(query),
        };
        if (preserveCompiledQuery) {
          store.dispatch(locationChanged(locationParams));
        } else {
          store.dispatch(addLocation(locationParams));
        }
      };

      // Extract query params from location.search
      const queryParams = new URLSearchParams(location.search);
      const query = Object.fromEntries(queryParams.entries());

      dispatchLocation(match.params, query);
    }, [match.params, location.search, store]);

    return <ExploreGridApp params={match.params} />;
  },
);

const AppWithStore = () => {
  const navigate = useNavigate();

  const onHistoryUpdated = (newUrl: string) => {
    navigate(newUrl);
  };

  const store = React.useMemo(() => create(onHistoryUpdated, externalDataProvider, {}), []);

  return (
    <Provider store={store}>
      <Routes>
        <Route path={BASE_PATH} element={<RouteHandler store={store} />} />
        <Route path={`${BASE_PATH}/page/:tab?/:subTab?`} element={<RouteHandler store={store} />} />
        <Route
          path={`${BASE_PATH}/query/:queryString?/:page?/:tab?/:subTab?`}
          element={<RouteHandler store={store} />}
        />
      </Routes>
    </Provider>
  );
};

const Main = () => {
  return (
    <BrowserRouter>
      <AppWithStore />
    </BrowserRouter>
  );
};

airbrake.init(_.get(window, 'serverConfig.airbrakeProjectId'), _.get(window, 'serverConfig.airbrakeKey'));

const externalDataProvider = (): ExternalVQEData => {
  const { view, possibleMainParent, canReadFromAllParents, joinsEnabled, clientContext } =
    window.initialState!;
  const editing = view!.publicationStage === 'unpublished';
  const parentView =
    possibleMainParent === undefined || possibleMainParent.id === view!.id ? none : some(possibleMainParent!);
  const fourfourToQuery = editing && parentView.isDefined ? parentView.get.id : view!.id;
  const queryString = editing && view!.queryString ? some(view!.queryString) : none;
  // these don't correspond to the actual selected columns of the query, but we will set the appState's columns after runQuery, which will happen on page load
  // so it's sort of a best guess until then.
  const columns: VQEColumn[] = viewColumnToVQEColumn(view!.columns);

  return {
    view: view!,
    columns,
    fourfourToQuery,
    editing,
    canReadFromAllParents: !!canReadFromAllParents,
    parentView,
    queryString,
    joinsEnabled: !!joinsEnabled,
    clientContext: clientContext!,
  };
};

window.addEventListener('load', function () {
  ReactDOM.render(<Main />, document.querySelector('[data-react-component="ExploreGrid"]'));
});
